import React from "react";
import {
  Box,
  Typography,
  SwipeableDrawer,
  IconButton,
  List,
  ListItem,
} from "@mui/material";
import { Badge } from "react-bootstrap";
import CloseIcon from "@mui/icons-material/Close";
import helpers from "../../hooks/HelperFunctions";
import { formatPayableDate } from "../../utils/payableDate";

const iOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

const parseApprovedBy = (approvedby) => {
  if (!approvedby || approvedby === "---") return null;
  const parts = approvedby.split(",");
  if (parts.length === 2) {
    return { name: parts[0], timestamp: parts[1] };
  }
  return { name: approvedby, timestamp: null };
};

const formatDateTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(parseInt(timestamp));
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const BADGE_STYLE = {
  padding: "4.2px 7.8px",
  fontSize: "12px",
  borderRadius: "8px",
};
function PaymentDrawer({ open, onClose, selectedItem, getPlatformNames }) {
  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      onOpen={() => { }}
      disableBackdropTransition={!iOS}
      disableDiscovery={iOS}
      swipeAreaWidth={30}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 400, md: 450 },
          p: 2,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          pb: 1,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Payment Details
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {selectedItem && (
        <Box sx={{ overflowY: "auto", maxHeight: "calc(100vh - 80px)" }}>
          <List dense disablePadding>
            {/* Campaign */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Campaign
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {selectedItem.campaign?.name || "-"}
              </Typography>
            </ListItem>

            {/* Vendor */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Vendor
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {selectedItem.isCompany ? selectedItem.companyName || "Vendor" : "---"}
              </Typography>
            </ListItem>

            {/* Platform */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Platform
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {getPlatformNames(selectedItem.platform)}
              </Typography>
            </ListItem>

            {/* Influencer */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Influencer
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {selectedItem.influencer?.username || selectedItem.influencer?.name || "-"}
              </Typography>
            </ListItem>

            {/* Deliverables */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Deliverables
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {selectedItem.deliverables || "-"}
              </Typography>
            </ListItem>

            {/* Comment */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Comment
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {selectedItem.comment || "---"}
              </Typography>
            </ListItem>

            {/* POC */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                POC
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {selectedItem.createdby?.fullName || "-"}
              </Typography>
            </ListItem>

            {/* Approved By */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Approved By
              </Typography>
              <Box sx={{ width: "65%", display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                {(() => {
                  const approvedData = parseApprovedBy(selectedItem.approvedby);
                  if (!approvedData) return <Typography variant="body2">---</Typography>;

                  return (
                    <>
                      <Typography variant="body2" sx={{ color: "#333", fontWeight: 500 }}>
                        {approvedData.name}
                      </Typography>
                      {approvedData.timestamp && (
                        <Badge
                          style={{
                            backgroundColor: "#6c757d",
                            color: "white",
                            ...BADGE_STYLE
                          }}
                        >
                          {formatDateTime(approvedData.timestamp)}
                        </Badge>
                      )}
                    </>
                  );
                })()}
              </Box>
            </ListItem>

            {/* Total Amount */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Total Amount
              </Typography>
              <Box sx={{ width: "65%" }}>
                <Badge
                  style={{
                    backgroundColor: "#198754",
                    color: "white",
                    ...BADGE_STYLE
                  }}
                >
                  ₹{selectedItem.cost?.toLocaleString() || "-"}
                </Badge>
              </Box>
            </ListItem>

            {/* GST Amount */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                GST Amount
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {selectedItem.isGst ? `₹${selectedItem.gstAmount?.toLocaleString() || "-"}` : "---"}
              </Typography>
            </ListItem>

            {/* Payable Amount */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Payable Amount
              </Typography>
              <Box sx={{ width: "65%" }}>
                <Badge
                  style={{
                    backgroundColor: "#ffc107",
                    color: "#000000",
                    ...BADGE_STYLE
                  }}
                >
                  ₹{selectedItem.payableAmount?.toLocaleString() || "-"}
                </Badge>
              </Box>
            </ListItem>
            {/* Payable Date */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Payable Date
              </Typography>
              <Box sx={{ width: "65%" }}>
                <Badge
                  style={{
                    backgroundColor: "#6c757d",
                    ...BADGE_STYLE
                  }}
                >
                  {selectedItem.payableDate ? formatPayableDate(selectedItem.payableDate) : "Add from Payment Queue Tab"}
                </Badge>
              </Box>
            </ListItem>

            {/* Invoice */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Invoice
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {selectedItem.invoice ? (
                  <a href={selectedItem.invoice} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2" }}>
                    View Invoice
                  </a>
                ) : (
                  "---"
                )}
              </Typography>
            </ListItem>

            {/* Email */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Email
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {selectedItem.influencer?.email || "---"}
              </Typography>
            </ListItem>

            {/* Phone */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Phone
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {helpers.getPrimaryContactNumber(selectedItem.influencer) || "---"}
              </Typography>
            </ListItem>

            {/* DOB */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                DOB
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {selectedItem.influencer?.dob || "---"}
              </Typography>
            </ListItem>

            {/* State */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                State
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {selectedItem.influencer?.state || "---"}
              </Typography>
            </ListItem>

            {/* Account Name */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Account Name
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {selectedItem.accountName || "---"}
              </Typography>
            </ListItem>

            {/* Account Number */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Account Number
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {selectedItem.accountNo || "---"}
              </Typography>
            </ListItem>

            {/* IFSC Code */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                IFSC Code
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {selectedItem.ifscCode || "---"}
              </Typography>
            </ListItem>

            {/* Pan Card Number */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Pan Card Number
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {selectedItem.pancard ? (
                  <a href={selectedItem.pancardFile} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2" }}>
                    {selectedItem.pancard}
                  </a>
                ) : (
                  "---"
                )}
              </Typography>
            </ListItem>

            {/* Pancard Name */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Pancard Name
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {selectedItem.pancardName || "---"}
              </Typography>
            </ListItem>

            {/* Paytm */}
            <ListItem divider sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Paytm
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {selectedItem.paytmNumber || "---"}
              </Typography>
            </ListItem>

            {/* Remarks */}
            <ListItem sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ width: "35%", fontWeight: 500, color: "#666" }}>
                Remarks
              </Typography>
              <Typography variant="body2" sx={{ width: "65%", color: "#333" }}>
                {selectedItem.remarks || "NA"}
              </Typography>
            </ListItem>
          </List>
        </Box>
      )}
    </SwipeableDrawer>
  );
}

export default React.memo(PaymentDrawer);